import React, { useState, useRef, useEffect } from 'react';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  Grid,
  Button,
  ButtonGroup,
  Stack,
  Alert,
  CircularProgress,
  TextField,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Description as DocumentIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
  PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Preview as PreviewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Description as WordIcon,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import { 
  Document as DocxDocument, 
  Packer as DocxPacker, 
  Paragraph as DocxParagraph, 
  HeadingLevel as DocxHeadingLevel,
  TextRun,
  Table,
  TableRow,
  TableCell,
  AlignmentType,
  WidthType,
  BorderStyle,
  Header as DocxHeader,
  Footer as DocxFooter,
  PageNumber,
  NumberFormat,
} from 'docx';
import { extractPlainTextForPdf, normalizePdfText, prepareSectionContentForExport } from '../../utils/formattingUtils';
import documentExportService from '../../services/documentExport.service';
import designStudioService from '../../services/designStudio.service';

// Helper to strip duplicate leading titles/headings from section content
const stripLeadingRepeatedTitleFromHtml = (html, title) => {
  if (!html || !title || typeof window === 'undefined') return html;

  try {
    const tempDiv = window.document.createElement('div');
    tempDiv.innerHTML = html;

    // Collect the first few meaningful children (elements or non-empty text nodes)
    const children = Array.from(tempDiv.childNodes).filter(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE ||
        (node.nodeType === Node.TEXT_NODE && node.textContent.trim())
    );

    const normalize = (s) => s.replace(/\s+/g, ' ').trim().toLowerCase();
    const normalizedTitle = normalize(title);

    const matches = [];
    for (let i = 0; i < Math.min(children.length, 5); i += 1) {
      const node = children[i];
      const text = node.textContent || '';
      if (normalize(text) === normalizedTitle) {
        matches.push(node);
      }
    }

    // Only remove the first heading if it appears at least twice near the top
    if (matches.length >= 2) {
      tempDiv.removeChild(matches[0]);
      return tempDiv.innerHTML;
    }

    return html;
  } catch {
    return html;
  }
};

// Build font config from saved structure or fall back to defaults
const buildFontConfig = (structure) => {
  const fontFamily = structure?.fonts?.fontFamily || 'Times New Roman';
  const titleSize  = structure?.fonts?.titleSize  || 14;
  const headingSize = structure?.fonts?.headingSize || 14;
  const bodySize   = structure?.fonts?.bodySize    || 12;

  return {
    title: { name: fontFamily, size: titleSize,   bold: true,  color: '000000', lineSpacing: 1.0  },
    heading: { name: fontFamily, size: headingSize, bold: true,  color: '000000', lineSpacing: 1.0  },
    subheading: { name: fontFamily, size: Math.max(bodySize, 12), bold: true,  color: '000000', lineSpacing: 1.0  },
    body: { name: fontFamily, size: bodySize,   bold: false, color: '000000', lineSpacing: 1.15 },
    tableHeader: { name: fontFamily, size: bodySize,   bold: true,  color: '2F5496', lineSpacing: 1.0  },
    tableCell: { name: fontFamily, size: bodySize,   bold: false, color: '000000', lineSpacing: 1.0  },
    list: { name: fontFamily, size: bodySize,   bold: false, color: '000000', lineSpacing: 1.15 },
  };
};

// Font configuration matching DocumentEditor
const FONT_CONFIG = buildFontConfig(null);

// Helper to compute a user-friendly document title from document data
const getFriendlyDocumentTitle = (document) => {
  if (!document) return 'Design Studio Document';
  if (document.title && document.title.trim()) return document.title.trim();

  const rawType = document.documentType || '';
  const typeLower = rawType.toLowerCase();

  if (typeLower === 'protocol') return 'Clinical Trial Protocol';
  if (typeLower === 'crf') return 'Case Report Form (CRF)';
  if (typeLower === 'icf') return 'Informed Consent Form (ICF)';
  if (typeLower === 'sap') return 'Statistical Analysis Plan (SAP)';

  // For arbitrary/custom types like "IDMC Charter", just use the label as-is
  return rawType || 'Design Studio Document';
};

const DocumentPreview = ({ document, sessionId, projectId, structure, onModifyRequest, onRegenerateSection }) => {
  const sectionRefs = useRef({});
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [mode, setMode] = useState('preview'); // 'edit', 'preview', or 'canvas'
  const [sectionPrompts, setSectionPrompts] = useState({}); // { sectionId: prompt }
  const [promptExpandedBySection, setPromptExpandedBySection] = useState({});
  const [generatingSection, setGeneratingSection] = useState(null);

  // Keyboard shortcut to toggle preview mode
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Ctrl/Cmd + P to toggle mode
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        event.preventDefault();
        const modes = ['preview', 'edit', 'canvas'];
        const currentIndex = modes.indexOf(mode);
        setMode(modes[(currentIndex + 1) % modes.length]);
      }
    };

    // Use window to avoid clashing with the `document` prop
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [mode]);

  const handleRestoreSections = async () => {
    if (!sessionId) return;
    
    setRestoring(true);
    setRestoreError(null);
    
    try {
      const result = await designStudioService.restoreSections(sessionId);
      if (result.success) {
        // Reload the page to show restored sections
        window.location.reload();
      }
    } catch (error) {
      console.error('Restore sections error:', error);
      setRestoreError(error.response?.data?.message || 'Failed to restore sections');
    } finally {
      setRestoring(false);
    }
  };

  const handleRegenerateSection = async (section) => {
    if (!section) return;
    
    const sectionId = section.sectionId || section.title;
    setGeneratingSection(sectionId);
    
    try {
      const prompt = sectionPrompts[sectionId] || '';

      if (onRegenerateSection) {
        // Preferred path: direct single-section regenerate via API
        await onRegenerateSection(sectionId, prompt);
      } else if (onModifyRequest) {
        // Fallback: use chat-based modification
        const message = prompt 
          ? `Regenerate section "${section.title}" with the following prompt: ${prompt}`
          : `Regenerate section "${section.title}" with improved content`;
        onModifyRequest(message);
      }
    } catch (error) {
      console.error('Error regenerating section:', error);
    } finally {
      setGeneratingSection(null);
    }
  };

  const handleExportToPDF = () => {
    if (!document) return;

    setExporting(true);

    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      let yPos = margin;

      const ensureSpace = (needed = 10) => {
        if (yPos + needed > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
        }
      };

      const addText = (text, fontSize = 12, isBold = false, spacing = 6) => {
        const normalized = normalizePdfText(text);
        if (!normalized) return;

        doc.setFontSize(fontSize);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');

        const lines = doc.splitTextToSize(normalized, contentWidth);
        const lineArray = Array.isArray(lines) ? lines : [lines];
        if (!lineArray.length) return;

        const lineHeight = fontSize * 0.45;
        ensureSpace(lineArray.length * lineHeight + spacing);
        doc.text(lineArray, margin, yPos);
        yPos += lineArray.length * lineHeight + spacing;
      };

      const renderExportHtml = (htmlContent, sectionTitle = '') => {
        const html = prepareSectionContentForExport(htmlContent, sectionTitle);
        if (!html.trim()) return;

        const container = window.document.createElement('div');
        container.innerHTML = html;

        const BLOCK_TAGS = new Set([
          'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'td', 'th', 'pre', 'blockquote',
        ]);
        const SKIP_TAGS = new Set(['script', 'style', 'thead', 'tbody', 'tfoot', 'colgroup', 'col']);

        const getNodeText = (node) => extractPlainTextForPdf(node);

        const walk = (node) => {
          if (node.nodeType === Node.TEXT_NODE) return;
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          const tag = node.tagName?.toLowerCase();
          if (SKIP_TAGS.has(tag)) {
            Array.from(node.childNodes).forEach(walk);
            return;
          }

          if (tag === 'h1') {
            addText(getNodeText(node), 16, true, 8);
            return;
          }
          if (tag === 'h2') {
            addText(getNodeText(node), 14, true, 6);
            return;
          }
          if (tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6') {
            addText(getNodeText(node), 12, true, 4);
            return;
          }
          if (tag === 'table') {
            node.querySelectorAll('tr').forEach((row) => {
              const cells = Array.from(row.querySelectorAll('th, td'))
                .map((cell) => getNodeText(cell))
                .filter(Boolean);
              if (cells.length) {
                const isHeader = Boolean(row.querySelector('th'));
                addText(cells.join('  |  '), isHeader ? 11 : 10, isHeader, 3);
              }
            });
            yPos += 4;
            return;
          }
          if (tag === 'ul' || tag === 'ol') {
            node.querySelectorAll(':scope > li').forEach((li) => {
              addText(`- ${getNodeText(li)}`, 12, false, 2);
            });
            return;
          }
          if (tag === 'br') {
            yPos += 4;
            return;
          }
          if (BLOCK_TAGS.has(tag)) {
            addText(getNodeText(node), 12, false, 4);
            return;
          }
          if (tag === 'div') {
            const hasBlockChild = Array.from(node.children).some((child) =>
              BLOCK_TAGS.has(child.tagName?.toLowerCase()) || child.tagName?.toLowerCase() === 'table'
            );
            if (!hasBlockChild) {
              addText(getNodeText(node), 12, false, 4);
              return;
            }
          }

          Array.from(node.childNodes).forEach(walk);
        };

        Array.from(container.childNodes).forEach(walk);
      };

      const docTitle = getFriendlyDocumentTitle(document).toUpperCase();
      addText(docTitle, 16, true, 10);

      if (Array.isArray(document.sections) && document.sections.length > 0) {
        document.sections.forEach((section, index) => {
          if (!section || !section.title) return;

          const sectionTitle = section.sectionNumber
            ? `${section.sectionNumber}. ${section.title}`
            : `${index + 1}. ${section.title}`;
          addText(sectionTitle.toUpperCase(), 14, true, 8);

          renderExportHtml(section.content || section.output || '', section.title);
          yPos += 4;
        });
      } else if (document.documentContent) {
        renderExportHtml(document.documentContent);
      }

      const docType = document.documentType || 'document';
      const timestamp = new Date().toISOString().split('T')[0];
      doc.save(`${docType}-${timestamp}.pdf`);

      setExporting(false);
    } catch (error) {
      console.error('PDF export error:', error);
      setExporting(false);
      alert('Failed to export PDF. Please try again.');
    }
  };

  // Convert HTML content to Word paragraphs with proper formatting
  // fontConfig defaults to module-level FONT_CONFIG (fixed defaults); pass FC for structure-aware export
  const convertHtmlToWordElements = (htmlContent, sectionTitle = '', fontConfig = FONT_CONFIG) => {
    const FC_LOCAL = fontConfig;
    if (!htmlContent) return [new DocxParagraph({ text: '' })];
    
    const elements = [];
    const tempDiv = window.document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Process content node by node
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
          // Check for numbered subsections in plain text
          const subsectionMatch = text.match(/^(\d+\.\d+(?:\.\d+)?)\s+(.+)$/);
          if (subsectionMatch) {
            const [, number, title] = subsectionMatch;
            const depth = (number.match(/\./g) || []).length;
            const headingFontConfig = depth === 1 ? FC_LOCAL.heading : FC_LOCAL.subheading;
            elements.push(new DocxParagraph({
              children: [new TextRun({
                text: `${number} ${title.trim()}`,
                font: headingFontConfig.name,
                size: headingFontConfig.size * 2,
                bold: true,
                color: headingFontConfig.color
              })],
              heading: depth === 1 ? DocxHeadingLevel.HEADING_2 : DocxHeadingLevel.HEADING_3,
              spacing: {
                line: Math.round(headingFontConfig.lineSpacing * 240),
                after: 180,
                before: 120
              }
            }));
            return;
          }
          
          // Check for main sections
          const mainSectionMatch = text.match(/^(\d+\.?\s+[A-Z][A-Z\s&]+)$/);
          if (mainSectionMatch) {
            elements.push(new DocxParagraph({
              children: [new TextRun({
                text: text,
                font: FC_LOCAL.heading.name,
                size: FC_LOCAL.heading.size * 2,
                bold: true,
                color: FC_LOCAL.heading.color
              })],
              heading: DocxHeadingLevel.HEADING_2,
              spacing: {
                line: Math.round(FC_LOCAL.heading.lineSpacing * 240),
                after: 240,
                before: 120
              }
            }));
            return;
          }
          
          // Regular paragraph text
          elements.push(new DocxParagraph({
            children: [new TextRun({
              text: text,
              font: FC_LOCAL.body.name,
              size: FC_LOCAL.body.size * 2,
              color: FC_LOCAL.body.color
            })],
            alignment: AlignmentType.JUSTIFIED,
            spacing: {
              line: Math.round(FC_LOCAL.body.lineSpacing * 240),
              before: 60
            }
          }));
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName?.toLowerCase();
        
        if (tagName === 'h1') {
          const text = node.textContent.trim();
          if (text) {
            elements.push(new DocxParagraph({
              children: [new TextRun({
                text: text,
                font: FC_LOCAL.title.name,
                size: FC_LOCAL.title.size * 2,
                bold: true,
                color: FC_LOCAL.title.color
              })],
              heading: DocxHeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
              spacing: {
                line: Math.round(FC_LOCAL.title.lineSpacing * 240),
                after: 240
              }
            }));
          }
        } else if (tagName === 'h2') {
          const text = node.textContent.trim();
          if (text) {
            elements.push(new DocxParagraph({
              children: [new TextRun({
                text: text,
                font: FC_LOCAL.heading.name,
                size: FC_LOCAL.heading.size * 2,
                bold: true,
                color: FC_LOCAL.heading.color
              })],
              heading: DocxHeadingLevel.HEADING_2,
              spacing: {
                line: Math.round(FC_LOCAL.heading.lineSpacing * 240),
                after: 240,
                before: 120
              }
            }));
          }
        } else if (tagName === 'h3' || tagName === 'h4' || tagName === 'h5' || tagName === 'h6') {
          const text = node.textContent.trim();
          if (text) {
            elements.push(new DocxParagraph({
              children: [new TextRun({
                text: text,
                font: FC_LOCAL.subheading.name,
                size: FC_LOCAL.subheading.size * 2,
                bold: true,
                color: FC_LOCAL.subheading.color
              })],
              heading: DocxHeadingLevel.HEADING_3,
              spacing: {
                line: Math.round(FC_LOCAL.subheading.lineSpacing * 240),
                after: 180,
                before: 120
              }
            }));
          }
        } else if (tagName === 'p') {
          const textRuns = [];
          const processTextNode = (n) => {
            if (n.nodeType === Node.TEXT_NODE) {
              const text = n.textContent.trim();
              if (text) {
                textRuns.push(new TextRun({
                  text: text,
                  font: FC_LOCAL.body.name,
                  size: FC_LOCAL.body.size * 2,
                  color: FC_LOCAL.body.color
                }));
              }
            } else if (n.nodeType === Node.ELEMENT_NODE) {
              const tag = n.tagName?.toLowerCase();
              const text = n.textContent.trim();
              if (text) {
                if (tag === 'strong' || tag === 'b') {
                  textRuns.push(new TextRun({
                    text: text,
                    font: FC_LOCAL.body.name,
                    size: FC_LOCAL.body.size * 2,
                    bold: true,
                    color: FC_LOCAL.body.color
                  }));
                } else if (tag === 'em' || tag === 'i') {
                  textRuns.push(new TextRun({
                    text: text,
                    font: FC_LOCAL.body.name,
                    size: FC_LOCAL.body.size * 2,
                    italics: true,
                    color: FC_LOCAL.body.color
                  }));
                } else {
                  Array.from(n.childNodes).forEach(processTextNode);
                }
              }
            }
          };
          Array.from(node.childNodes).forEach(processTextNode);
          
          if (textRuns.length > 0) {
            elements.push(new DocxParagraph({
              children: textRuns,
              alignment: AlignmentType.JUSTIFIED,
              spacing: {
                line: Math.round(FC_LOCAL.body.lineSpacing * 240),
                before: 60
              }
            }));
          }
        } else if (tagName === 'table') {
          const tableRows = node.querySelectorAll('tr');
          const rows = [];
          let maxCols = 0;

          tableRows.forEach((row) => {
            maxCols = Math.max(maxCols, row.querySelectorAll('th, td').length);
          });

          tableRows.forEach((row, rowIndex) => {
            const cells = [];
            const tableCells = row.querySelectorAll('th, td');
            const isHeader = rowIndex === 0 || row.querySelector('th');

            tableCells.forEach((cell) => {
              const cellText = (cell.textContent || '').replace(/\s+/g, ' ').trim();
              const cellFontConfig = isHeader ? FC_LOCAL.tableHeader : FC_LOCAL.tableCell;
              cells.push(new TableCell({
                children: [new DocxParagraph({
                  children: [new TextRun({
                    text: cellText,
                    font: cellFontConfig.name,
                    size: cellFontConfig.size * 2,
                    bold: cellFontConfig.bold,
                    color: cellFontConfig.color
                  })],
                  spacing: {
                    line: Math.round(cellFontConfig.lineSpacing * 240)
                  },
                  alignment: AlignmentType.LEFT
                })],
                width: {
                  size: maxCols > 0 ? Math.floor(100 / maxCols) : 100,
                  type: WidthType.PERCENTAGE,
                },
                margins: {
                  top: 100,
                  bottom: 100,
                  left: 150,
                  right: 150,
                },
                verticalAlign: 'center',
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                  bottom: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                  left: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                  right: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                }
              }));
            });

            while (cells.length < maxCols) {
              cells.push(new TableCell({
                children: [new DocxParagraph({ text: '' })],
                width: {
                  size: maxCols > 0 ? Math.floor(100 / maxCols) : 100,
                  type: WidthType.PERCENTAGE,
                },
              }));
            }

            if (cells.length > 0) {
              rows.push(new TableRow({ children: cells }));
            }
          });

          if (rows.length > 0) {
            elements.push(new Table({
              rows,
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                bottom: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                left: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                right: { style: BorderStyle.SINGLE, size: 8, color: '000000' },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: '000000' },
                insideVertical: { style: BorderStyle.SINGLE, size: 4, color: '000000' }
              }
            }));
            elements.push(new DocxParagraph({ text: '' }));
          }
        } else if (tagName === 'thead' || tagName === 'tbody' || tagName === 'tfoot') {
          Array.from(node.childNodes).forEach(processNode);
        } else if (tagName === 'ul' || tagName === 'ol') {
          const listItems = node.querySelectorAll('li');
          listItems.forEach(item => {
            const text = item.textContent.trim();
            if (text) {
              elements.push(new DocxParagraph({
                children: [new TextRun({
                  text: `• ${text}`,
                  font: FC_LOCAL.list.name,
                  size: FC_LOCAL.list.size * 2,
                  color: FC_LOCAL.list.color
                })],
                alignment: AlignmentType.JUSTIFIED,
                spacing: {
                  line: Math.round(FC_LOCAL.list.lineSpacing * 240),
                  before: 60
                },
                indent: { left: 240 }
              }));
            }
          });
        } else if (tagName === 'li') {
          // Already handled in ul/ol
        } else {
          // Process child nodes for other elements
          Array.from(node.childNodes).forEach(processNode);
        }
      }
    };
    
    Array.from(tempDiv.childNodes).forEach(processNode);
    
    // If no elements were created, add empty paragraph
    if (elements.length === 0) {
      elements.push(new DocxParagraph({ text: '' }));
    }
    
    return elements;
  };

  const handleExportToWord = async () => {
    if (!document) return;

    const hasSections = Array.isArray(document.sections) && document.sections.length > 0;
    const hasDocumentContent = Boolean(document.documentContent && String(document.documentContent).trim());
    if (!hasSections && !hasDocumentContent) return;

    setExporting(true);
    
    try {
      // Build font config from saved structure (live, not the module-level constant)
      const FC = buildFontConfig(structure);

      const children = [];

      // Title based on actual document title if available, otherwise by type
      const titleText = getFriendlyDocumentTitle(document);

      children.push(
        new DocxParagraph({
          children: [new TextRun({
            text: titleText,
            font: FC.title.name,
            size: FC.title.size * 2,
            bold: true,
            color: FC.title.color
          })],
          heading: DocxHeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: {
            line: Math.round(FC.title.lineSpacing * 240),
            after: 360
          }
        }),
        new DocxParagraph({ text: '' })
      );

      // Table of Contents placeholder if enabled
      if (structure?.includeToc && hasSections) {
        children.push(
          new DocxParagraph({
            children: [new TextRun({
              text: 'Table of Contents',
              font: FC.heading.name,
              size: FC.heading.size * 2,
              bold: true,
            })],
            heading: DocxHeadingLevel.HEADING_2,
            spacing: { after: 200 }
          })
        );
        document.sections.forEach((sec, idx) => {
          if (!sec || !sec.title) return;
          const label = sec.sectionNumber ? `${sec.sectionNumber}. ${sec.title}` : `${idx + 1}. ${sec.title}`;
          children.push(
            new DocxParagraph({
              children: [new TextRun({ text: label, font: FC.body.name, size: FC.body.size * 2 })],
              spacing: { after: 100 }
            })
          );
        });
        children.push(new DocxParagraph({ text: '', pageBreakBefore: true }));
      }

      // Process each section with proper formatting
      if (hasSections) {
      document.sections.forEach((section, idx) => {
        if (!section || !section.title) return;

        const sectionContent = section.content || section.output || '';
        if (!sectionContent.trim()) return;

        // Page break before each section except first
        if (idx > 0) {
          children.push(
            new DocxParagraph({
              children: [],
              pageBreakBefore: true,
            })
          );
        }

        // Section title
        const sectionTitle = section.sectionNumber
          ? `${section.sectionNumber}. ${section.title}`
          : section.title;
        
        children.push(
          new DocxParagraph({
            children: [new TextRun({
              text: sectionTitle,
              font: FC.heading.name,
              size: FC.heading.size * 2,
              bold: true,
              color: FC.heading.color
            })],
            heading: DocxHeadingLevel.HEADING_2,
            spacing: {
              line: Math.round(FC.heading.lineSpacing * 240),
              after: 240,
              before: 120
            }
          })
        );

        const processedContent = prepareSectionContentForExport(sectionContent, section.title);
        const wordElements = convertHtmlToWordElements(processedContent, section.title, FC);
        children.push(...wordElements);
        
        // Add spacing after section
        children.push(new DocxParagraph({ text: '' }));
      });
      } else if (hasDocumentContent) {
        const processedContent = prepareSectionContentForExport(document.documentContent);
        children.push(...convertHtmlToWordElements(processedContent, '', FC));
      }

      // Build header / footer sections from saved structure
      const docxSectionProperties = {};

      if (structure?.header?.enabled && structure?.header?.text) {
        docxSectionProperties.headers = {
          default: new DocxHeader({
            children: [
              new DocxParagraph({
                children: [new TextRun({
                  text: structure.header.text,
                  font: FC.body.name,
                  size: FC.body.size * 2,
                  color: '555555',
                })],
                alignment: AlignmentType.RIGHT,
              }),
            ],
          }),
        };
      }

      if (structure?.footer?.enabled && structure?.footer?.text) {
        docxSectionProperties.footers = {
          default: new DocxFooter({
            children: [
              new DocxParagraph({
                children: [
                  new TextRun({
                    text: `${structure.footer.text}    `,
                    font: FC.body.name,
                    size: FC.body.size * 2,
                    color: '555555',
                  }),
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    font: FC.body.name,
                    size: FC.body.size * 2,
                  }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        };
      }

      const docx = new DocxDocument({
        sections: [{ ...docxSectionProperties, children }],
      });

      const blob = await DocxPacker.toBlob(docx);

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;

      // Derive a filename from the friendly title
      const baseName = getFriendlyDocumentTitle(document)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'design-studio-document';
      const filename = `${baseName}.docx`;
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Save export metadata for Templates tab (only if we have projectId)
      if (projectId) {
        try {
          let sectionsData;

          if (Array.isArray(document.sections) && document.sections.length > 0) {
            // Sectioned documents (protocol / CRF / ICF / etc.)
            sectionsData = document.sections.map((section) => ({
              sectionNumber: section.sectionNumber,
              title: section.title,
              output: section.content,
              content: section.content,
              subsections: [],
            }));
          } else {
            // Non-sectioned / generic documents: treat whole doc as a single section
            sectionsData = [
              {
                sectionNumber: null,
                title: getFriendlyDocumentTitle(document),
                output: document.documentContent || '',
                content: document.documentContent || '',
                subsections: [],
              },
            ];
          }

          // Convert blob to base64 (non-blocking UX but awaited here)
          const toBase64 = (blob) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result.split(',')[1]);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });

          const documentBase64 = await toBase64(blob);

          // Fetch prompt from Design Studio session if available
          let generatedPrompt = null;
          let designStudioSessionId = null;
          if (sessionId) {
            try {
              const session = await designStudioService.getSession(sessionId);
              if (session?.session?.generatedPrompt) {
                generatedPrompt = session.session.generatedPrompt;
                designStudioSessionId = sessionId;
              }
            } catch (promptError) {
              console.warn('⚠️ Could not fetch prompt from session (non-blocking):', promptError);
            }
          }

          const metadata = {
            source: 'design-studio',
            documentType: document.documentType || 'protocol',
            sessionId: sessionId || null,
            designStudioTitle: document.title || null,
            generatedPrompt: generatedPrompt,
            designStudioSessionId: designStudioSessionId,
          };

          await documentExportService.saveProtocolExport(
            projectId,
            null,
            'DOCX',
            filename,
            sectionsData,
            metadata,
            documentBase64,
            blob.size
          );
        } catch (saveError) {
          console.warn('⚠️ Failed to save Design Studio export for Templates tab (non-blocking):', saveError);
        }
      }
      
      setExporting(false);
    } catch (error) {
      console.error('Word export error:', error);
      setExporting(false);
      alert('Failed to export Word document. Please try again.');
    }
  };

  if (!document) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No document generated yet
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Generate a document through chat to see it here
        </Typography>
      </Box>
    );
  }

  // Check if document has very few sections (likely lost sections)
  const hasLostSections = document.sections && document.sections.length < 3 && sessionId;

  // Section-based Document Preview (protocol, CRF, or any custom type with sections)
  if (document.sections && document.sections.length > 0) {
    return (
      <Box>
        {/* Restore Alert */}
        {hasLostSections && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={restoring ? <CircularProgress size={16} /> : <RestoreIcon />}
                onClick={handleRestoreSections}
                disabled={restoring}
              >
                {restoring ? 'Restoring...' : 'Restore All Sections'}
              </Button>
            }
          >
            {restoreError ? (
              <Typography variant="body2">{restoreError}</Typography>
            ) : (
              <Typography variant="body2">
                It looks like some sections may be missing. Click "Restore All Sections" to recover them from chat history.
              </Typography>
            )}
          </Alert>
        )}

        {/* Document Header */}
        <Paper 
          sx={{ 
            p: 2, 
            mb: 2, 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DocumentIcon />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {getFriendlyDocumentTitle(document)}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                {document.totalSections || document.sections.length} sections generated
              </Typography>
            </Box>
            <Stack 
              direction="row" 
              spacing={1} 
              sx={{ 
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                alignItems: 'center',
                flexShrink: 0,
                minWidth: 0
              }}
            >
              {/* Mode Toggle */}
              <Tooltip title={`${mode === 'edit' ? 'Switch to Preview Mode' : mode === 'preview' ? 'Switch to Canvas Mode' : 'Switch to Edit Mode'} (Ctrl+P)`}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={mode === 'edit' ? <VisibilityIcon /> : mode === 'preview' ? <PreviewIcon /> : <EditIcon />}
                  onClick={() => {
                    const modes = ['preview', 'edit', 'canvas'];
                    const currentIndex = modes.indexOf(mode);
                    setMode(modes[(currentIndex + 1) % modes.length]);
                  }}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                    px: 1.5,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                      borderColor: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                >
                  {mode === 'edit' ? 'Preview' : mode === 'preview' ? 'Canvas' : 'Edit'}
                </Button>
              </Tooltip>
              {onModifyRequest && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => onModifyRequest('I want to modify some sections in the protocol document')}
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                      px: 1.5,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                        borderColor: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  >
                    Modify
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => onModifyRequest('I want to add new sections to the protocol document')}
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                      px: 1.5,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                        borderColor: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  >
                    Add
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <WordIcon />}
                onClick={handleExportToWord}
                disabled={exporting}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  whiteSpace: 'nowrap',
                  minWidth: 'auto',
                  px: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                {exporting ? 'Exporting...' : 'Word'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
                onClick={handleExportToPDF}
                disabled={exporting}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  whiteSpace: 'nowrap',
                  minWidth: 'auto',
                  px: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                {exporting ? 'Exporting...' : 'PDF'}
              </Button>
            </Stack>
          </Box>
        </Paper>

        {/* Section Navigation Chips - Matching DocumentEditor style */}
        {document.sections && document.sections.length > 0 && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'white',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {document.sections
                .sort((a, b) => {
                  const aMatch = (a.sectionNumber || a.title || '').match(/^(\d+)/);
                  const bMatch = (b.sectionNumber || b.title || '').match(/^(\d+)/);
                  if (aMatch && bMatch) {
                    return parseInt(aMatch[1], 10) - parseInt(bMatch[1], 10);
                  }
                  return (a.title || '').localeCompare(b.title || '');
                })
                .map((section, idx) => (
                  <Chip
                    key={section.sectionId || idx}
                    label={section.title}
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const element = sectionRefs.current[section.sectionId || idx];
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    sx={{
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  />
                ))}
            </Box>
          </Paper>
        )}

        {/* Section Content - Matching DocumentEditor style */}
        {mode === 'canvas' ? (
          // Canvas Mode - Simplified canvas view
          <Box>
            {document.sections.map((section, idx) => (
              <Paper
                key={section.sectionId || idx}
                id={`section-${section.sectionId || idx}`}
                ref={(el) => {
                  if (el) sectionRefs.current[section.sectionId || idx] = el;
                }}
                sx={{
                  mb: 2,
                  p: 3,
                  borderRadius: 2,
                  bgcolor: '#f8f9fa',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
                  {section.sectionNumber ? `${section.sectionNumber}. ` : ''}{section.title}
                </Typography>
                <Box
                  component="div"
                  sx={{
                    fontFamily: '"Times New Roman", serif',
                    fontSize: '12pt',
                    lineHeight: 1.6,
                    color: '#1a1a1a',
                    '& p': { mb: 1.5, lineHeight: 1.6 },
                    '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1, fontWeight: 700, fontSize: '1em' },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      const title =
                        section.sectionNumber && section.title
                          ? `${section.sectionNumber}. ${section.title}`
                          : section.title || '';
                      const raw = section.content || '<p style="color: #999; font-style: italic;">No content available</p>';
                      return sanitizeRichHtml(stripLeadingRepeatedTitleFromHtml(raw, title));
                    })(),
                  }}
                />
              </Paper>
            ))}
          </Box>
        ) : (
          <Box>
            {document.sections.map((section, idx) => {
              const sectionId = section.sectionId || idx;
              const isGenerating = generatingSection === sectionId;
              
              return (
                <Paper
                  key={sectionId}
                  id={`section-${sectionId}`}
                  ref={(el) => {
                    if (el) sectionRefs.current[sectionId] = el;
                  }}
                  sx={{
                    mb: 3,
                    p: 3,
                    borderRadius: 2,
                    position: 'relative',
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      background: 'rgba(255, 255, 255, 0.92)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  {/* Section Header - Matching DocumentEditor style */}
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 400, 
                      color: 'primary.main'
                    }}>
                      Section: {section.sectionNumber ? `${section.sectionNumber}. ` : ''}{section.title}
                    </Typography>
                  </Box>

                  {/* Prompt Editor - Only in Edit Mode */}
                  {mode === 'edit' && onModifyRequest && (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          borderRadius: 2,
                          border: '1px solid rgba(0,0,0,0.08)',
                          backgroundColor: '#fafafa',
                          p: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            setPromptExpandedBySection((prev) => ({
                              ...prev,
                              [sectionId]: !prev[sectionId],
                            }));
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            Prompt
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {sectionPrompts[sectionId] ? (sectionPrompts[sectionId].length > 50 ? sectionPrompts[sectionId].substring(0, 50) + '...' : sectionPrompts[sectionId]) : 'No custom prompt'}
                            </Typography>
                            {promptExpandedBySection[sectionId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </Box>
                        </Box>
                        <Collapse in={promptExpandedBySection[sectionId]}>
                          <Box sx={{ mt: 1.5, display: 'flex', gap: 2, alignItems: 'stretch', flexDirection: 'column' }}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={4}
                              maxRows={12}
                              value={sectionPrompts[sectionId] || ''}
                              onChange={(e) => setSectionPrompts((prev) => ({
                                ...prev,
                                [sectionId]: e.target.value
                              }))}
                              variant="outlined"
                              size="small"
                              placeholder="Enter a custom prompt for this section. Leave empty to use default prompt when regenerating."
                            />
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={isGenerating}
                                onClick={() => handleRegenerateSection(section)}
                                startIcon={isGenerating ? <CircularProgress size={16} /> : <RefreshIcon />}
                                sx={{ flex: 1, minWidth: 150 }}
                              >
                                {isGenerating ? 'Regenerating...' : 'Regenerate Section'}
                              </Button>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    </Box>
                  )}

                  {/* Section Content - Professional document formatting */}
              <Box
                component="div"
                sx={{
                  fontFamily: '"Times New Roman", serif',
                  fontSize: '12pt',
                  lineHeight: 1.6,
                  color: '#1a1a1a',
                  '& p': { 
                    mb: 2, 
                    lineHeight: 1.6,
                    textAlign: 'justify',
                    fontSize: '12pt',
                  },
                  '& h1': { 
                    mt: 3, 
                    mb: 2, 
                    fontWeight: 700, 
                    fontSize: '14pt',
                    fontFamily: '"Times New Roman", serif',
                    color: '#1a1a1a',
                  },
                  '& h2': { 
                    mt: 3, 
                    mb: 1.5, 
                    fontWeight: 700, 
                    fontSize: '13pt',
                    fontFamily: '"Times New Roman", serif',
                    color: '#1a1a1a',
                  },
                  '& h3': { 
                    mt: 2, 
                    mb: 1.5, 
                    fontWeight: 600, 
                    fontSize: '12pt',
                    fontFamily: '"Times New Roman", serif',
                    color: '#1a1a1a',
                  },
                  '& h4': { 
                    mt: 2, 
                    mb: 1, 
                    fontWeight: 600, 
                    fontSize: '12pt',
                    fontFamily: '"Times New Roman", serif',
                    color: '#1a1a1a',
                  },
                  '& ul, & ol': { 
                    pl: 3, 
                    mb: 2,
                    lineHeight: 1.8,
                  },
                  '& li': { 
                    mb: 0.8,
                    lineHeight: 1.6,
                  },
                  '& strong, & b': {
                    fontWeight: 700,
                  },
                  '& em, & i': {
                    fontStyle: 'italic',
                  },
                  '& table': { 
                    width: '100%', 
                    borderCollapse: 'collapse', 
                    mb: 2, 
                    mt: 2,
                    fontSize: '11pt',
                  },
                  '& th, & td': { 
                    border: '1px solid #ddd', 
                    padding: '10px', 
                    textAlign: 'left',
                    lineHeight: 1.5,
                  },
                  '& th': { 
                    bgcolor: '#f5f5f5', 
                    fontWeight: 700,
                    fontSize: '11pt',
                  },
                }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeRichHtml(section.content) || '<p style="color: #999; font-style: italic;">No content available</p>',
                  }}
                />
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>
    );
  }

  // ICF Document Preview - Similar to Protocol (with sections)
  if (document.documentType === 'icf' && document.sections) {
    return (
      <Box>
        {/* Document Header */}
        <Paper 
          sx={{ 
            p: 2, 
            mb: 2, 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DocumentIcon />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Informed Consent Form (ICF)
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                {document.totalSections || document.sections.length} sections generated
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              {/* Mode Toggle */}
              <Tooltip title={`${mode === 'edit' ? 'Switch to Preview Mode' : mode === 'preview' ? 'Switch to Canvas Mode' : 'Switch to Edit Mode'} (Ctrl+P)`}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={mode === 'edit' ? <VisibilityIcon /> : mode === 'preview' ? <PreviewIcon /> : <EditIcon />}
                  onClick={() => {
                    const modes = ['preview', 'edit', 'canvas'];
                    const currentIndex = modes.indexOf(mode);
                    setMode(modes[(currentIndex + 1) % modes.length]);
                  }}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                      borderColor: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                >
                  {mode === 'edit' ? 'Preview' : mode === 'preview' ? 'Canvas' : 'Edit'}
                </Button>
              </Tooltip>
              {onModifyRequest && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => onModifyRequest('I want to modify some sections in the ICF document')}
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                        borderColor: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  >
                    Modify
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => onModifyRequest('I want to add new sections to the ICF document')}
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                        borderColor: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  >
                    Add Sections
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <WordIcon />}
                onClick={handleExportToWord}
                disabled={exporting}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                {exporting ? 'Exporting...' : 'Export Word'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
                onClick={handleExportToPDF}
                disabled={exporting}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                {exporting ? 'Exporting...' : 'Export PDF'}
              </Button>
            </Stack>
          </Box>
        </Paper>

        {/* Section Navigation Chips - Matching DocumentEditor style */}
        {document.sections && document.sections.length > 0 && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'white',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {document.sections
                .sort((a, b) => {
                  const aMatch = (a.sectionNumber || a.title || '').match(/^(\d+)/);
                  const bMatch = (b.sectionNumber || b.title || '').match(/^(\d+)/);
                  if (aMatch && bMatch) {
                    return parseInt(aMatch[1], 10) - parseInt(bMatch[1], 10);
                  }
                  return (a.title || '').localeCompare(b.title || '');
                })
                .map((section, idx) => (
                  <Chip
                    key={section.sectionId || idx}
                    label={section.title}
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const element = sectionRefs.current[section.sectionId || idx];
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    sx={{
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  />
                ))}
            </Box>
          </Paper>
        )}

        {/* Section Content - Matching DocumentEditor style */}
        {mode === 'canvas' ? (
          // Canvas Mode - Simplified canvas view
          <Box>
            {document.sections.map((section, idx) => (
              <Paper
                key={section.sectionId || idx}
                id={`section-${section.sectionId || idx}`}
                ref={(el) => {
                  if (el) sectionRefs.current[section.sectionId || idx] = el;
                }}
                sx={{
                  mb: 2,
                  p: 3,
                  borderRadius: 2,
                  bgcolor: '#f8f9fa',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
                  {section.sectionNumber ? `${section.sectionNumber}. ` : ''}{section.title}
                </Typography>
                <Box
                  component="div"
                  sx={{
                    fontFamily: '"Arial", "Calibri", "Verdana", sans-serif',
                    fontSize: '12pt',
                    lineHeight: 1.8,
                    color: '#1a1a1a',
                    '& p': { mb: 1.5, lineHeight: 1.8 },
                    '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1, fontWeight: 700, fontSize: '1em' },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeRichHtml(section.content) || '<p style="color: #999; font-style: italic;">No content available</p>',
                  }}
                />
              </Paper>
            ))}
          </Box>
        ) : (
          <Box>
            {document.sections.map((section, idx) => {
              const sectionId = section.sectionId || idx;
              const isGenerating = generatingSection === sectionId;
              
              return (
                <Paper
                  key={sectionId}
                  id={`section-${sectionId}`}
                  ref={(el) => {
                    if (el) sectionRefs.current[sectionId] = el;
                  }}
                  sx={{
                    mb: 3,
                    p: 3,
                    borderRadius: 2,
                    position: 'relative',
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      background: 'rgba(255, 255, 255, 0.92)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  {/* Section Header - Matching DocumentEditor style */}
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 400, 
                      color: 'primary.main'
                    }}>
                      Section: {section.sectionNumber ? `${section.sectionNumber}. ` : ''}{section.title}
                    </Typography>
                  </Box>

                  {/* Prompt Editor - Only in Edit Mode */}
                  {mode === 'edit' && onModifyRequest && (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          borderRadius: 2,
                          border: '1px solid rgba(0,0,0,0.08)',
                          backgroundColor: '#fafafa',
                          p: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            setPromptExpandedBySection((prev) => ({
                              ...prev,
                              [sectionId]: !prev[sectionId],
                            }));
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            Prompt
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {sectionPrompts[sectionId] ? (sectionPrompts[sectionId].length > 50 ? sectionPrompts[sectionId].substring(0, 50) + '...' : sectionPrompts[sectionId]) : 'No custom prompt'}
                            </Typography>
                            {promptExpandedBySection[sectionId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </Box>
                        </Box>
                        <Collapse in={promptExpandedBySection[sectionId]}>
                          <Box sx={{ mt: 1.5, display: 'flex', gap: 2, alignItems: 'stretch', flexDirection: 'column' }}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={4}
                              maxRows={12}
                              value={sectionPrompts[sectionId] || ''}
                              onChange={(e) => setSectionPrompts((prev) => ({
                                ...prev,
                                [sectionId]: e.target.value
                              }))}
                              variant="outlined"
                              size="small"
                              placeholder="Enter a custom prompt for this section. Leave empty to use default prompt when regenerating."
                            />
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={isGenerating}
                                onClick={() => handleRegenerateSection(section)}
                                startIcon={isGenerating ? <CircularProgress size={16} /> : <RefreshIcon />}
                                sx={{ flex: 1, minWidth: 150 }}
                              >
                                {isGenerating ? 'Regenerating...' : 'Regenerate Section'}
                              </Button>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    </Box>
                  )}

                  {/* Section Content - ICF formatting */}
              <Box
                component="div"
                sx={{
                  fontFamily: '"Arial", "Calibri", "Verdana", sans-serif',
                  fontSize: '12pt',
                  lineHeight: 1.8,
                  color: '#1a1a1a',
                  '& p': { 
                    mb: 2, 
                    lineHeight: 1.8,
                    textAlign: 'justify',
                    fontSize: '12pt',
                  },
                  '& h1': { 
                    mt: 3, 
                    mb: 2, 
                    fontWeight: 700, 
                    fontSize: '14pt',
                    fontFamily: '"Arial", "Calibri", "Verdana", sans-serif',
                    color: '#1a1a1a',
                    textTransform: 'uppercase',
                  },
                  '& h2': { 
                    mt: 3, 
                    mb: 1.5, 
                    fontWeight: 700, 
                    fontSize: '14pt',
                    fontFamily: '"Arial", "Calibri", "Verdana", sans-serif',
                    color: '#1a1a1a',
                    textTransform: 'uppercase',
                  },
                  '& h3': { 
                    mt: 2, 
                    mb: 1.5, 
                    fontWeight: 600, 
                    fontSize: '12pt',
                    fontFamily: '"Arial", "Calibri", "Verdana", sans-serif',
                    color: '#1a1a1a',
                    textTransform: 'uppercase',
                  },
                  '& ul, & ol': { 
                    pl: 3, 
                    mb: 2,
                    lineHeight: 1.8,
                  },
                  '& li': { 
                    mb: 0.8,
                    lineHeight: 1.8,
                  },
                  '& strong, & b': {
                    fontWeight: 700,
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: section.content || '<p style="color: #999; font-style: italic;">No content available</p>',
                }}
              />
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>
    );
  }

  // SAP/Other Document Preview - Similar to SAPEditor
  return (
    <Box>
      {/* Document Header */}
      <Paper 
        sx={{ 
          p: 2, 
          mb: 2, 
          bgcolor: 'primary.main', 
          color: 'primary.contrastText',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DocumentIcon />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {getFriendlyDocumentTitle(document)}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              Generated {document.generatedAt ? new Date(document.generatedAt).toLocaleDateString() : 'recently'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {onModifyRequest && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => onModifyRequest(`I want to modify the ${document.documentType} document`)}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                      borderColor: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                >
                  Modify
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => onModifyRequest(`I want to add more content to the ${document.documentType} document`)}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                      borderColor: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                >
                  Add Content
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={handleExportToWord}
              disabled={exporting}
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                },
                '&:disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              Export Word
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
              onClick={handleExportToPDF}
              disabled={exporting}
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                },
                '&:disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              {exporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Document Content - Professional clinical document formatting */}
      <Paper
        sx={{
          p: 4,
          bgcolor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 0,
          minHeight: '400px',
          maxHeight: 'calc(100vh - 200px)',
          overflow: 'auto',
          boxShadow: 'none',
          // Simulate document page
          '@media print': {
            boxShadow: 'none',
            border: 'none',
          },
        }}
      >
        <Box
          component="div"
          sx={{
            fontFamily: document.documentType === 'icf' 
              ? '"Arial", "Calibri", "Verdana", sans-serif' 
              : '"Times New Roman", serif',
            fontSize: document.documentType === 'icf' ? '12pt' : '12pt',
            lineHeight: 1.6,
            color: '#1a1a1a',
            maxWidth: '8.5in', // Standard letter width
            margin: '0 auto',
            '& p': { 
              mb: 2, 
              lineHeight: document.documentType === 'icf' ? 1.8 : 1.6,
              textAlign: 'justify',
              fontSize: '12pt',
            },
            '& h1': { 
              mt: 4, 
              mb: 2, 
              fontWeight: 700, 
              fontSize: document.documentType === 'icf' ? '14pt' : '14pt',
              color: '#1a1a1a',
              textTransform: document.documentType === 'icf' ? 'uppercase' : 'none',
            },
            '& h2': { 
              mt: 3, 
              mb: 1.5, 
              fontWeight: 700, 
              fontSize: document.documentType === 'icf' ? '14pt' : '13pt',
              color: '#1a1a1a',
              textTransform: document.documentType === 'icf' ? 'uppercase' : 'none',
            },
            '& h3': { 
              mt: 2, 
              mb: 1.5, 
              fontWeight: 600, 
              fontSize: '12pt',
              color: '#1a1a1a',
            },
            '& ul, & ol': { 
              pl: 3, 
              mb: 2,
              lineHeight: 1.8,
            },
            '& li': { 
              mb: 1,
              lineHeight: 1.6,
            },
            '& strong, & b': {
              fontWeight: 700,
            },
            '& em, & i': {
              fontStyle: 'italic',
            },
            '& table': { 
              width: '100%', 
              borderCollapse: 'collapse', 
              mb: 2, 
              mt: 2,
              fontSize: '11pt',
            },
            '& th, & td': { 
              border: '1px solid #ddd', 
              padding: '10px', 
              textAlign: 'left',
              lineHeight: 1.5,
            },
            '& th': { 
              bgcolor: '#f5f5f5', 
              fontWeight: 700,
              fontSize: '11pt',
            },
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeRichHtml(document.documentContent) || '<p style="color: #999; font-style: italic;">No content available</p>',
          }}
        />
      </Paper>
    </Box>
  );
};

export default DocumentPreview;

